/**
 * @file useLegalDocument.ts
 * @description Карточка юридического документа: загрузка, перечитывание и
 * замена ответом мутации (phase19.16, SB139–SB144).
 *
 * Один источник правды — ответ API: после сохранения, публикации, загрузки
 * файла или правки состава страница кладёт пришедшую карточку целиком
 * (`setDoc`), а не правит поля руками. `reload` — после 409
 * «изменён в другом окне».
 */

import { useCallback, useEffect, useState } from "react";

import { getLegalDoc, type LegalDocumentDto } from "../../lib/api-legal";
import { legalErrorText } from "../../lib/legal/legal-errors";

export interface LegalDocumentState {
  doc: LegalDocumentDto | null;
  setDoc: (doc: LegalDocumentDto) => void;
  loading: boolean;
  error: string | null;
  /** Перечитать; результат — свежая карточка или null при сбое. */
  reload: () => Promise<LegalDocumentDto | null>;
}

export function useLegalDocument(id: string | null): LegalDocumentState {
  const [doc, setDoc] = useState<LegalDocumentDto | null>(null);
  const [loading, setLoading] = useState(id !== null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<LegalDocumentDto | null> => {
    if (!id) return null;
    setLoading(true);
    setError(null);
    try {
      const fresh = await getLegalDoc(id);
      setDoc(fresh);
      return fresh;
    } catch (err) {
      setError(legalErrorText(err, "Не удалось загрузить документ"));
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { doc, setDoc, loading, error, reload };
}
